import { translate } from "pdf-lib";
import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Card, Tabs, Tab, Badge } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import { useTranslate } from "../hooks/LanguageProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
const backendUrl = process.env.REACT_APP_BACKEND_URL;

// 🔹 Styles onglets
const tabStyles = `
.nav-tabs {
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
}
.custom-tabs .nav-link {
  color: #555;
  font-weight: 500;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-bottom: none;
  margin-right: 5px;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  transition: all 0.2s ease-in-out;
}
.custom-tabs .nav-link.active {
  background-color: #ffffff;
  color: #0d6efd;
  border-color: #dee2e6 #dee2e6 #ffffff;
  font-weight: 600;
  box-shadow: 0 -2px 5px rgba(13, 110, 253, 0.15);
}
.custom-tabs .tab-content {
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-top: none;
  padding: 20px;
  border-radius: 0 0 0.5rem 0.5rem;
}
`;

const DriverBehaviorConfig: React.FC<{ from: "admin" | "user" }> = ({ from }) => {
  const { id_user } = useParams<{ id_user?: string }>();
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const activeUserId = from === "admin" && id_user ? Number(id_user) : currentUser?.id_user;

  const { translate } = useTranslate();

  const [config, setConfig] = useState<any>({
    id_user: activeUserId || 0,
    config_name: "default",
  });
  const [buttonClicked, setButtonClicked] = useState(false);

  useEffect(() => {
    if (activeUserId) getDriverConfig(activeUserId);
  }, [activeUserId]);

  // 🧩 utilitaires
  const extractNumber = (v: any) => {
    if (!v) return "";
    const n = parseFloat(v.toString().replace(",", ".").replace(/[^\d.]/g, ""));
    return isNaN(n) ? "" : n;
  };
  const extractNightHours = (s: string) => {
    if (!s) return { start: "22:00", end: "06:00" };
    const m = s.match(/(\d{1,2})h?(\d{0,2})?-(\d{1,2})h?(\d{0,2})?/);
    if (!m) return { start: "22:00", end: "06:00" };
    const h1 = m[1].padStart(2, "0");
    const h2 = m[3].padStart(2, "0");
    return { start: `${h1}:${m[2] || "00"}`, end: `${h2}:${m[4] || "00"}` };
  };

  const [isUpdate, setIsUpdate] = useState(false); // 🟢 Flag de détection global

  const getDriverConfig = async (userId: number) => {
    try {
      const res = await fetch(`${backendUrl}/api/driver-behavior/${userId}`, { mode: "cors" });
      const data = await res.json();
      const { start, end } = extractNightHours(data.night_driving_rules);

      // ✅ Détection automatique du mode Update
      if (data && Object.keys(data).length > 0) {
        setIsUpdate(true); // Config existante détectée
      } else {
        setIsUpdate(false); // Aucune config => création
      }

      const cleaned = {
        id_config: data.id_config || 0, // ✅ On garde l’id s’il existe
        id_user: userId,
        config_name: data.config_name || "default",
        max_continues_driving: extractNumber(data.max_continues_driving) || 4,
        min_break_after_continues_driving: extractNumber(data.min_break_after_continues_driving) || 15,
        max_driving_hours_24h: extractNumber(data.max_driving_hours_24h) || 10,
        min_rest_period_24h: extractNumber(data.min_rest_period_24h) || 10,
        max_on_duty_hours_24h: extractNumber(data.max_on_duty_hours_24h) || 14,
        min_break_7days: extractNumber(data.min_break_7days) || 24,
        night_start: start,
        night_end: end,
        speeding_max_speed: Number(data.speeding_max_speed) || 80,
        speeding_max_duration: Number(data.speeding_max_duration) || 20,
        harsh_braking: data.harsh_braking || "2.2 m/s²",
        harsh_acceleration: data.harsh_acceleration || "2.2 m/s²",
        harsh_cornering: data.harsh_cornering || "",
        score_harsh_braking_incident: data.score_harsh_braking_incident || "",
        score_speeding_duration: data.score_speeding_duration || "",
        score_harsh_acceleration_incident: data.score_harsh_acceleration_incident || "",
        score_exceeding_driving_hours: data.score_exceeding_driving_hours || "",
        score_driving_distance: data.score_driving_distance || "",
        exceeding_driving_hours_time_above_max: data.exceeding_driving_hours_time_above_max || "",
        exceeding_driving_hours_time_below_rest: data.exceeding_driving_hours_time_below_rest || "",
      };
      setConfig(cleaned);
    } catch (e) {
      console.error(e);
      setIsUpdate(false); // ⚠️ En cas d’erreur, on revient sur création
      toast.warn("Erreur chargement configuration", { transition: Bounce });
    }
  };


  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setConfig((p: any) => ({ ...p, [name]: value }));
  };


  const saveConfig = async () => {
    if (buttonClicked) return;
    setButtonClicked(true);

    const endpoint = isUpdate
      ? `${backendUrl}/api/driver-behavior/update`
      : `${backendUrl}/api/driver-behavior/add`;

    try {

      // 🧩 Fusion automatique du créneau de conduite de nuit
      const nightRule = `${(config.night_start || "22:00").replace(":", "h")}-${(config.night_end || "06:00").replace(":", "h")}`;


      // 🧠 Payload complet et cohérent
      const payload = {
        ...config,
        night_driving_rules: nightRule,
      };



      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        mode: "cors",
      });

      if (!res.ok) throw new Error("Erreur sauvegarde");

      // 🆕 Après création → bascule automatique en mode update
      if (!isUpdate) {
        const result = await res.json().catch(() => ({}));
        setConfig((prev: any) => ({
          ...prev,
          id_config: result?.result?.insertId || prev.id_config || 0,
        }));
        setIsUpdate(true);
      }

      toast.success(
        isUpdate
          ? "✅ Configuration mise à jour avec succès"
          : "🆕 Configuration créée avec succès",
        { transition: Bounce }
      );
    } catch (err) {
      console.error("❌ Erreur sauvegarde config :", err);
      toast.warn("Erreur lors de la sauvegarde", { transition: Bounce });
    } finally {
      setButtonClicked(false);
    }
  };


  const cancelClicked = () => navigate("/driver-behavior-list");

  const MetricSlider = ({
    label,
    name,
    min,
    max,
    step,
    unit,
    value,
    thresholds,
  }: any) => {
    const color =
      thresholds && value >= thresholds.good
        ? "success"
        : thresholds && value >= thresholds.warning
          ? "warning"
          : "danger";
    return (
      <Form.Group controlId={name} className="mb-4">
        <Form.Label className="fw-semibold">{label}</Form.Label>
        <div className="d-flex align-items-center gap-3">
          <Form.Range
            min={min}
            max={max}
            step={step}
            name={name}
            value={value || 0}
            onChange={handleChange}
            style={{ flex: 1 }}
          />
          <Form.Control
            type="number"
            name={name}
            value={value || ""}
            min={min}
            max={max}
            step={step}
            onChange={handleChange}
            style={{ width: "80px" }}
          />
          <span>{unit}</span>
        </div>
        {thresholds && (
          <div className={`text-${color}`} style={{ fontSize: "0.85rem" }}>
            {color === "success"
              ? "✅ Bon équilibre"
              : color === "warning"
                ? "⚠️ À surveiller"
                : "🚨 Hors seuil"}
          </div>
        )}
      </Form.Group>
    );
  };

  return (
    <div className="mt-4">
      <style>{tabStyles}</style>
      <h4>
        {from === "admin"
          ? "Modifier la configuration du conducteur"
          : "Configurer le comportement de conduite"}
      </h4>

      <Form>
        <Tabs defaultActiveKey="hos" id="driver-behavior-tabs" className="mb-4 custom-tabs">
          {/* HOS */}
          <Tab eventKey="hos" title="Règles de conduite (HOS)">
            <Card className="p-4 mt-3 shadow-sm">
              <Row>
                <Col md={6}>
                  <MetricSlider
                    label="Durée maximale de conduite continue"
                    name="max_continues_driving"
                    min={1}
                    max={10}
                    step={0.5}
                    unit="h"
                    value={config.max_continues_driving}
                    thresholds={{ good: 8, warning: 4 }}
                  />
                </Col>
                <Col md={6}>
                  <MetricSlider
                    label="Pause minimale après conduite"
                    name="min_break_after_continues_driving"
                    min={5}
                    max={90}
                    step={5}
                    unit="min"
                    value={config.min_break_after_continues_driving}
                    thresholds={{ good: 45, warning: 15 }}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <MetricSlider
                    label="Heures max sur 24h"
                    name="max_driving_hours_24h"
                    min={4}
                    max={24}
                    step={1}
                    unit="h"
                    value={config.max_driving_hours_24h}
                  />
                </Col>
                <Col md={6}>
                  <MetricSlider
                    label="Repos minimal sur 24h"
                    name="min_rest_period_24h"
                    min={1}
                    max={12}
                    step={0.5}
                    unit="h"
                    value={config.min_rest_period_24h}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <MetricSlider
                    label="Service max sur 24h"
                    name="max_on_duty_hours_24h"
                    min={4}
                    max={24}
                    step={1}
                    unit="h"
                    value={config.max_on_duty_hours_24h}
                  />
                </Col>
                <Col md={6}>
                  <MetricSlider
                    label="Pause minimale sur 7 jours"
                    name="min_break_7days"
                    min={8}
                    max={72}
                    step={1}
                    unit="h"
                    value={config.min_break_7days}
                  />
                </Col>
              </Row>
              <Row>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>🌙 Début conduite de nuit</Form.Label>
                      <TimePicker
                        ampm={false} // ✅ format 24h
                        value={dayjs(config.night_start || "22:00", "HH:mm")}
                        onChange={(newValue) =>
                          handleChange({
                            target: { name: "night_start", value: newValue?.format("HH:mm") },
                          })
                        }
                        slotProps={{
                          textField: { fullWidth: true, size: "small", variant: "outlined" },
                        }}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>🌅 Fin conduite de nuit</Form.Label>
                      <TimePicker
                        ampm={false}
                        value={dayjs(config.night_end || "06:00", "HH:mm")}
                        onChange={(newValue) =>
                          handleChange({
                            target: { name: "night_end", value: newValue?.format("HH:mm") },
                          })
                        }
                        slotProps={{
                          textField: { fullWidth: true, size: "small", variant: "outlined" },
                        }}
                      />
                    </Form.Group>
                  </Col>
                </LocalizationProvider>
              </Row>
            </Card>
          </Tab>

          {/* Comportement */}
          <Tab eventKey="behavior" title="Comportement du conducteur">
            <Card className="p-4 mt-3 shadow-sm">
              <Row>
                <Col md={6}>
                  <MetricSlider
                    label="Seuil freinage brusque"
                    name="harsh_braking"
                    min={0.5}
                    max={5}
                    step={0.1}
                    unit="m/s²"
                    value={extractNumber(config.harsh_braking)}
                    thresholds={{ good: 2.5, warning: 1.5 }}
                  />
                </Col>
                <Col md={6}>
                  <MetricSlider
                    label="Seuil accélération brusque"
                    name="harsh_acceleration"
                    min={0.5}
                    max={5}
                    step={0.1}
                    unit="m/s²"
                    value={extractNumber(config.harsh_acceleration)}
                    thresholds={{ good: 2.5, warning: 1.5 }}
                  />
                </Col>
                <Col md={6}>
                  <MetricSlider
                    label="Vitesse maximale"
                    name="speeding_max_speed"
                    min={50}
                    max={200}
                    step={5}
                    unit="km/h"
                    value={config.speeding_max_speed}
                    thresholds={{ good: 120, warning: 90 }}
                  />
                </Col>
                <Col md={6}>
                  <MetricSlider
                    label="Durée excès vitesse"
                    name="speeding_max_duration"
                    min={5}
                    max={120}
                    step={5}
                    unit="s"
                    value={config.speeding_max_duration}
                    thresholds={{ good: 60, warning: 20 }}
                  />
                </Col>
              </Row>
            </Card>
          </Tab>

          {/* Scorecard */}
          {/* Scorecard / Points amélioré */}
          <Tab eventKey="score" title="Scorecard / Points">
            <Card className="p-4 mt-3 shadow-sm" style={{ borderRadius: "12px", background: "#f9fafb" }}>
              <h6 className="fw-bold mb-3">⚖️ Pondération des points de conduite</h6>

              <Row>
                <Col md={6}>
                  <MetricSlider
                    label="Points freinage brusque"
                    name="score_harsh_braking_incident"
                    min={0}
                    max={10}
                    step={1}
                    unit="pts"
                    value={extractNumber(config.score_harsh_braking_incident) || 1}
                    thresholds={{ good: 3, warning: 6 }}
                  />
                </Col>
                <Col md={6}>
                  <MetricSlider
                    label="Points accélération brusque"
                    name="score_harsh_acceleration_incident"
                    min={0}
                    max={10}
                    step={1}
                    unit="pts"
                    value={extractNumber(config.score_harsh_acceleration_incident) || 1}
                    thresholds={{ good: 3, warning: 6 }}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <MetricSlider
                    label="Points excès de vitesse"
                    name="score_speeding_duration"
                    min={0}
                    max={10}
                    step={1}
                    unit="pts"
                    value={extractNumber(config.score_speeding_duration) || 1}
                    thresholds={{ good: 3, warning: 6 }}
                  />
                </Col>
                <Col md={6}>
                  <MetricSlider
                    label="Points dépassement heures de conduite"
                    name="score_exceeding_driving_hours"
                    min={0}
                    max={10}
                    step={1}
                    unit="pts"
                    value={extractNumber(config.score_exceeding_driving_hours) || 1}
                    thresholds={{ good: 3, warning: 6 }}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <MetricSlider
                    label="Impact distance parcourue sur le score final"
                    name="score_driving_distance"
                    min={0}
                    max={5}
                    step={0.5}
                    unit="x"
                    value={extractNumber(config.score_driving_distance) || 1}
                    thresholds={{ good: 2, warning: 4 }}
                  />
                </Col>
              </Row>

              <div className="text-end mt-3">
                <Badge bg="primary" pill>
                  Score global estimé :{" "}
                  {(
                    ((extractNumber(config.score_harsh_braking_incident) || 1) +
                      (extractNumber(config.score_harsh_acceleration_incident) || 1) +
                      (extractNumber(config.score_speeding_duration) || 1) +
                      (extractNumber(config.score_exceeding_driving_hours) || 1)) *
                    (extractNumber(config.score_driving_distance) || 1)
                  ).toFixed(1)}{" "}
                  pts
                </Badge>
              </div>
            </Card>
          </Tab>

          {/* Règles supplémentaires amélioré */}
          <Tab eventKey="extra" title="Règles supplémentaires">
            <Card className="p-4 mt-3 shadow-sm" style={{ borderRadius: "12px", background: "#f9fafb" }}>
              <h6 className="fw-bold mb-3">📋 Conditions HOS et paramètres additionnels</h6>

              <Row>
                <Col md={6}>
                  <Form.Group controlId="exceeding_driving_hours_time_above_max" className="mb-4">
                    <Form.Label>Temps au-dessus de la durée maximale de conduite</Form.Label>
                    <Form.Select
                      name="exceeding_driving_hours_time_above_max"
                      value={config.exceeding_driving_hours_time_above_max || ""}
                      onChange={handleChange}
                    >
                      <option value="">Choisir...</option>
                      <option value="Avertissement automatique">Avertissement automatique</option>
                      <option value="Blocage du démarrage du véhicule">Blocage du démarrage du véhicule</option>
                      <option value="Notification au superviseur">Notification au superviseur</option>
                      <option value="Aucune action">Aucune action</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="exceeding_driving_hours_time_below_rest" className="mb-4">
                    <Form.Label>Temps en dessous de la période de repos minimum</Form.Label>
                    <Form.Select
                      name="exceeding_driving_hours_time_below_rest"
                      value={config.exceeding_driving_hours_time_below_rest || ""}
                      onChange={handleChange}
                    >
                      <option value="">Choisir...</option>
                      <option value="Avertissement sonore conducteur">Avertissement sonore conducteur</option>
                      <option value="Blocage du véhicule pendant 8h">Blocage du véhicule pendant 8h</option>
                      <option value="Rapport automatique envoyé au système">Rapport automatique envoyé</option>
                      <option value="Aucune action">Aucune action</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group controlId="config_name">
                    <Form.Label>Nom de la configuration</Form.Label>
                    <Form.Control
                      type="text"
                      name="config_name"
                      value={config.config_name || ""}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <div className="mt-4">
                    <Badge bg="info" pill>
                      Dernière mise à jour :{" "}
                      {config.updated_at
                        ? new Date(config.updated_at).toLocaleString("fr-FR")
                        : "non enregistrée"}
                    </Badge>
                  </div>
                </Col>
              </Row>
            </Card>
          </Tab>

        </Tabs>

        <div className="mt-4 d-flex justify-content-end">
          <Button variant="secondary" onClick={cancelClicked} className="me-2">
            {translate("Cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={saveConfig}
            disabled={buttonClicked}
          >
            {config.id_config ? translate("Update") : translate("Add")}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default DriverBehaviorConfig;
